// AF-3.16 — shared workspace + entity selection for Brand Growth OS pages.
//
// Mirrors the existing app-wide convention (see CreativeIntelligencePage.js):
// `clientsAPI.list()` populates a dropdown, first client selected by
// default. This hook adds an entity selector on top, since every AF-2.x/
// AF-3.x route is entity-scoped. The selected workspaceId always comes
// from this authenticated, server-verified list -- never trusted from a
// URL parameter (spec Section 24: workspace ID must never be treated as
// trusted solely because it appears in the URL).
//
// P1.7: the workspace-selection half (clients/workspaceId/setWorkspaceId/
// loadingClients) is now useWorkspaceSelection() itself, factored out so
// workspace-wide pages that don't need entity drill-down can reuse the
// identical mechanism. This hook's own external shape/behavior is
// unchanged for its existing consumers.

import { useState, useEffect, useCallback } from 'react';
import { entitiesAPI } from '@/lib/intelligenceApi';
import { useWorkspaceSelection } from './useWorkspaceSelection';

// P2.5 -- `initialEntityType` is optional and additive. Every existing
// caller (25 of them) calls useBrandContext() with no args and keeps the
// unchanged 'product' default. OpportunityPortfolioPage opts into 'brand'
// because the SoM -> Signal -> Opportunity chain the founder now triggers
// from the Signals page always produces entity_type="brand" opportunities
// (_ingest_som_measurement_into_knowledge) -- landing on 'product' left
// the just-created opportunity behind a manual entity-type switch.
export function useBrandContext({ initialEntityType = 'product' } = {}) {
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const [entities, setEntities] = useState([]);
  const [entityType, setEntityType] = useState(initialEntityType);
  const [entityId, setEntityId] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);

  const reloadEntities = useCallback(() => {
    if (!workspaceId) return;
    setLoadingEntities(true);
    entitiesAPI.list(workspaceId, { entityType })
      .then((r) => {
        const list = r.data?.entities || r.data || [];
        setEntities(list);
        if (list.length > 0 && !list.find((e) => e.id === entityId)) setEntityId(list[0].id);
        if (list.length === 0) setEntityId('');
      })
      .catch(() => setEntities([]))
      .finally(() => setLoadingEntities(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, entityType]);

  useEffect(() => { reloadEntities(); }, [reloadEntities]);

  const selectedEntity = entities.find((e) => e.id === entityId) || null;

  return {
    clients, workspaceId, setWorkspaceId,
    entities, entityType, setEntityType, entityId, setEntityId, selectedEntity,
    loadingClients, loadingEntities, reloadEntities,
    ready: !!workspaceId && !!entityId,
  };
}
